/*
 * Copyright (c) 2021 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */
import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { HttpHeaders } from '@angular/common/http';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';

const TOKEN_END_POINT = '/npr/token';

const TOKEN_TTL = 600_000;

const log = new Logger('NPRAuth');

/**
 * NPR Authentication - token create/refresh service
 */
@Injectable({
  providedIn: 'root'
})
export class NPRAuthentication {
  private timer: any;

  private credentials = {
    token: undefined,
    refreshToken: undefined,
    tokenExpTime: undefined,
    refreshTokenExpTime: undefined,
    isSystemToken: false
  };

  private status = 'inprogress';

  public onsync: any = undefined;
  public onfailure: any = undefined;
  public onready: any = undefined;

  constructor(
    private dataService: ProxyDataService,
    private notify: NotifyService,
    private translate: TranslateService
  ) {}

  private setToken(response: any) {
    this.credentials.token = response.token;
    this.credentials.refreshToken = response.refreshToken;
    this.credentials.tokenExpTime = response.tokenExpTime;
    this.credentials.refreshTokenExpTime = response.refreshTokenExpTime;

    this.status = 'success';
    log.debug('Success: ', this.credentials);
  }

  private isValid(response: any) {
    return response && response.token && response.refreshToken && response.tokenExpTime && response.refreshTokenExpTime;
  }

  private refreshToken() {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: 'Bearer ' + this.credentials.refreshToken,
        Pragma: 'no-cache'
      })
    };
    this.dataService.httpGet(TOKEN_END_POINT + '?' + Date.now(), options).then(
      response => {
        if (this.isValid(response)) {
          this.setToken(response);
          if (this.onsync !== undefined) {
            this.onsync();
          }
        } else {
          this.handleError(response);
        }

        log.info('The token got refreshed');
      },
      error => {
        log.error('Cannot refresh the token: ' + error);
        this.handleError(error);
      }
    );
  }

  public clear() {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
    }
  }

  private setUpdater() {
    const ttl =
      _.isNumber(this.credentials.tokenExpTime) && this.credentials.tokenExpTime > 0
        ? this.credentials.tokenExpTime
        : TOKEN_TTL;
    this.clear();
    this.timer = setInterval(() => this.refreshToken(), ttl - 60_000);
    log.info('Refreshing the token in ' + ttl + ' ms');
  }

  createToken(user: any, permissions: Array<string>) {
    const data = {
      userId: user.id,
      userFirstname: user.firstname,
      userLastname: user.lastname,
      permissions: permissions,
      requester: 'GEHC/WFM'
    };
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    log.debug('Token request payload = ', data);
    this.dataService.httpPost(TOKEN_END_POINT, data, options).then(
      response => {
        if (this.isValid(response)) {
          this.setToken(response);
          if (this.onready !== undefined) {
            this.onready();
          }
          this.setUpdater();
        } else {
          this.handleError(response);
        }
      },
      error => {
        this.handleError(error);
        if (error.status >= 500) {
          log.error('Retry to get token in 30 seconds');
          const createTokenTimeout = setTimeout(() => {
            this.createToken(user, permissions);
            clearTimeout(createTokenTimeout);
          }, 30_000);
        }
      }
    );
  }

  useSystemToken() {
    this.credentials.isSystemToken = true;
    this.credentials.token = 'WFM.System.Token';
    this.status = 'success';
  }

  isSystemToken() {
    return this.credentials.isSystemToken;
  }

  private handleError(info: any) {
    log.error('Failed: ' + JSON.stringify(info));

    this.status = 'failed';
    const errorMessage =
      info.status >= 500
        ? 'di.desktopIntegration.error.nprServiceError'
        : 'di.desktopIntegration.error.nprauthenticationfailed';
    this.notify.error(
      this.translate.instant(errorMessage),
      this.translate.instant('di.desktopIntegration.titleDictateDisabled')
    );
    clearInterval(this.timer);

    if (this.onfailure !== undefined) {
      this.onfailure();
    }
  }

  getToken() {
    return this.credentials.token;
  }

  getStatus() {
    return this.status;
  }

  isFailed() {
    return this.status === 'failed';
  }

  createUUID() {
    let dt = Date.now();
    const randomValues = new Uint8Array(16);
    crypto.getRandomValues(randomValues);
    let randomIndex = 0;
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (dt + randomValues[randomIndex++ % 16]) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
  }
}
